import { useAuth0 } from '@auth0/auth0-react';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';

import { setBearerTokenGetter } from './api/auth-token';
import { App } from './App';
import { Auth0ProviderWithNavigate } from './Auth0ProviderWithNavigate';
import { ConfigProvider } from './contexts/ConfigContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { VcmProvider } from './contexts/VcmContext';
import { AppThemeProvider } from './contexts/ThemeModeContext';

function isLocalDev(): boolean {
  return !window.opengovConfig;
}

/**
 * Authenticated app: redirect to Auth0 when needed, then register the token
 * getter so the Effect transport attaches `Authorization: Bearer <token>`.
 */
function AuthenticatedApp() {
  const { getAccessTokenSilently, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      void loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    setBearerTokenGetter(isAuthenticated ? getAccessTokenSilently : null);
  }, [getAccessTokenSilently, isAuthenticated]);

  if (isLoading || !isAuthenticated) {
    return <div>Loading...</div>;
  }

  return (
    <VcmProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </VcmProvider>
  );
}

/** Local dev: no Auth0, no token — requests go out unauthenticated. */
function LocalDevApp() {
  return (
    <VcmProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </VcmProvider>
  );
}

function Root() {
  return (
    <AppThemeProvider>
      <LanguageProvider>
        {isLocalDev() ? (
          <LocalDevApp />
        ) : (
          <Auth0ProviderWithNavigate>
            <AuthenticatedApp />
          </Auth0ProviderWithNavigate>
        )}
      </LanguageProvider>
    </AppThemeProvider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

createRoot(root).render(
  <StrictMode>
    <ConfigProvider>
      <Root />
    </ConfigProvider>
  </StrictMode>,
);
